import { useCallback } from 'react';
import styled from 'styled-components';
import useTranslations from '../../../translation/use-translations';
import { ARCHIVED, CANCELLED, ENDED } from '../constants/we-spin-stream-states.js';
import CountDown from '../../../../utils/count-down.js';
import LiveDot from './live-dot.js';
import { getWeekDayDateAndMonth } from '../utils/we-spin-time-helper.js';
import { WE_SPIN_START_TIME_TEXT_KEYS } from '../constants/we-spin-text-keys.js';

const StyledSpanWithDisabled = styled.span`
	color: ${({ isEventDisabled }) => isEventDisabled && 'rgba(var(--pending), 1)'};
`;

const SHOW_COUNTDOWN_THRESHOLD = 1000 * 60 * 60 * 2;

const StartTime = ({ start, state, prefix, isEventDisabled, isFreeWeSpin, isOnline }) => {
	const { translations, isLoadingTranslations } = useTranslations(WE_SPIN_START_TIME_TEXT_KEYS);

	let prefixStarts = prefix === '' ? '' : translations.WE_SPIN_START_TIME_STARTS_AT;

	if (isEventDisabled && isFreeWeSpin) {
		prefixStarts = `${translations.WE_SPIN_START_TIME_OPENS} `;
	}

	/**
	 * Renderer Function for the countdown
	 * @param {object} props
	 * @param {numer} props.hours | hours left
	 * @param {numer} props.minutes | minutes left
	 * @param {numer} props.seconds | seconds left
	 * @param {boolean} props.completed | completed true | false
	 */
	const countDownRenderer = useCallback(
		({ total, hours, minutes, seconds, completed }) => {
			const prefixCountdown = isEventDisabled
				? translations.WE_SPIN_START_TIME_OPENS_IN
				: translations.WE_SPIN_START_TIME_STARTS_IN;
			if (isLoadingTranslations) return null;
			if (completed && !isFreeWeSpin) {
				return <span>{translations.WE_SPIN_START_TIME_STARTS_NOW}</span>;
			}
			if (completed && isFreeWeSpin) {
				return <span>{translations.WE_SPIN_START_TIME_FREE_WESPIN_STARTS_NOW}</span>;
			}
			// count down view config to show hours minuts and seconds
			const hourString = hours > 0 ? `${hours} h ` : '';
			const minString = minutes > 0 ? `${minutes} min ` : '';
			// only show seconds if its is less than 10 minutes
			const secString = total < 600000 ? `${seconds} s` : '';

			return (
				<StyledSpanWithDisabled isEventDisabled={isEventDisabled}>
					{`${prefixCountdown} ${hourString}${minString}${secString}`}
				</StyledSpanWithDisabled>
			);
		},
		[
			isLoadingTranslations,
			translations.WE_SPIN_START_TIME_OPENS_IN,
			translations.WE_SPIN_START_TIME_STARTS_IN,
			translations.WE_SPIN_START_TIME_STARTS_NOW,
			translations.WE_SPIN_START_TIME_FREE_WESPIN_STARTS_NOW,
			isEventDisabled,
			isFreeWeSpin
		]
	);

	if (isLoadingTranslations) return null;

	// How the tick should be updated in Countdown
	const updateFrequency = ({ total }) => (total > 630000 ? 30000 : 500);

	const startDate = new Date(start);
	const nowDate = new Date();
	const datePart = `${getWeekDayDateAndMonth(start, translations)} - `;

	const formattedStartTime = `${datePart}${startDate.getHours()}:${`00${startDate.getMinutes()}`.slice(-2)}`;

	if (isOnline) {
		return (
			<div className="u-flex u-align-vert u-relative">
				<LiveDot />
				<span>{translations.WE_SPIN_START_TIME_LIVE}</span>
			</div>
		);
	}

	if (state === ENDED || state === ARCHIVED) {
		return <span>{translations.WE_SPIN_START_TIME_STREAM_ENDED}</span>;
	}
	if (state === CANCELLED) {
		return <span>{formattedStartTime}</span>;
	}
	if (startDate - nowDate < SHOW_COUNTDOWN_THRESHOLD) {
		return <CountDown date={startDate.getTime()} renderer={countDownRenderer} updateFrequency={updateFrequency} />;
	}
	return <span>{`${prefixStarts} ${formattedStartTime}`}</span>;
};

export default StartTime;
